/* ============================================================
   BLUE SALON — DELIVERY LOCATION GATE
   sections/delivery-location.liquid renders one of two pickers; this file
   drives whichever is on the page.

   MAP MODE (a Google Maps API key is set) — address autocomplete, "use my
   current location", and a draggable pin. The chosen point fills three hidden
   cart-attribute inputs, and the section's own <form> does the saving: POST
   /cart carries `attributes[...]` alongside the `checkout` param, so one
   native submit records the location AND continues to checkout. No Ajax.

   AREA LIST MODE (no key) — plain radios that need no script at all; the only
   enhancements here are remembering the choice and filtering a long list.

   Two Google surfaces are used, and only two: Places for text predictions and
   the Geocoder for every coordinate/address resolution (including turning a
   prediction's placeId into a point). Keeping resolution in one place means
   one shape of address data to normalise, and it avoids Place Details billing.
   Places itself is tried newest-API-first and degrades twice — see
   fetchSuggestions.
   ============================================================ */
(function () {
  "use strict";

  var deliveryI18n = (window.BS_I18N && window.BS_I18N.delivery) || {};
  function deliveryText(key, fallback) {
    return deliveryI18n[key] || fallback;
  }

  var root = document.querySelector("[data-delivery-location]");
  if (!root) return;

  var form = root.querySelector("[data-dloc-form]");
  if (!form) return;

  var STORAGE_KEY = form.dataset.storageKey || "bs_delivery_location";

  function readStored() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      // Older builds stored a bare area name; a JSON parse failure just means
      // one of those, which is still useful as the area.
      try {
        var parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : { area: String(parsed) };
      } catch (err) {
        return { area: raw };
      }
    } catch (err) {
      // Private browsing / storage disabled — the cart attribute is the real
      // record, so losing the convenience copy costs nothing.
      return null;
    }
  }

  function writeStored(value) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch (err) {
      /* ignore */
    }
  }

  /* ==========================================================
     AREA LIST MODE
     ========================================================== */
  if (!form.dataset.apiKey) {
    var inputs = Array.prototype.slice.call(form.querySelectorAll("[data-dloc-input]"));
    if (!inputs.length) return;

    var checkedInput = function () {
      return inputs.filter(function (input) { return input.checked; })[0];
    };

    // The cart attribute wins — if the section already rendered a checked
    // radio, that's the area on the cart right now and localStorage must not
    // override it.
    if (!checkedInput()) {
      var stored = readStored();
      if (stored && stored.area) {
        inputs.forEach(function (input) {
          if (input.value === stored.area) input.checked = true;
        });
      }
    }

    var filterWrap = root.querySelector("[data-dloc-filter]");
    var filterInput = root.querySelector("[data-dloc-filter-input]");
    var noMatch = root.querySelector("[data-dloc-no-match]");
    var zones = [];

    var applyFilter = function () {
      var term = filterInput ? filterInput.value.trim().toLowerCase() : "";
      var shown = 0;
      zones.forEach(function (zone) {
        // Notes are matched too ("next-day") — a shopper searching by how fast
        // it arrives is as likely as one searching by area name.
        var hit = !term || zone.name.indexOf(term) !== -1 || zone.note.indexOf(term) !== -1;
        zone.el.hidden = !hit;
        // A hidden `required` radio is a dead end: the browser refuses to
        // submit but can't focus the control to say why ("not focusable"), so
        // validation only ever applies to rows the shopper can see. The submit
        // handler below covers the everything-filtered-out case.
        if (zone.input) zone.input.required = hit;
        if (hit) shown++;
      });
      if (noMatch) noMatch.hidden = shown !== 0;
    };

    if (filterWrap && filterInput) {
      filterWrap.hidden = false;
      zones = Array.prototype.slice.call(root.querySelectorAll("[data-dloc-zone]")).map(function (zone) {
        var nameEl = zone.querySelector(".dloc-zone-name");
        var noteEl = zone.querySelector(".dloc-zone-text small");
        return {
          el: zone,
          input: zone.querySelector("[data-dloc-input]"),
          name: (nameEl ? nameEl.textContent : "").trim().toLowerCase(),
          note: (noteEl ? noteEl.textContent : "").trim().toLowerCase()
        };
      });
      filterInput.addEventListener("input", applyFilter);
    }

    // Skipping is a plain submit here — an unchecked radio group posts nothing,
    // and formnovalidate steps over `required`. The flag only stops the
    // filtered-out guard below from blocking it.
    var skippingZones = false;
    root.querySelectorAll("[data-dloc-skip]").forEach(function (button) {
      button.addEventListener("click", function () { skippingZones = true; });
    });

    form.addEventListener("submit", function (e) {
      if (skippingZones) return;
      var chosen = checkedInput();
      if (chosen) {
        writeStored({ area: chosen.value });
        return;
      }
      // Only reachable while a filter is hiding rows — unfiltered, the radios'
      // own `required` stops this before the event fires. Clear the filter so
      // every area is back on screen, then let the browser raise its own
      // validation message against a control it can focus.
      e.preventDefault();
      if (filterInput) {
        filterInput.value = "";
        applyFilter();
      }
      if (typeof form.reportValidity === "function") form.reportValidity();
    });

    return;
  }

  /* ==========================================================
     MAP MODE
     ========================================================== */
  var API_KEY = form.dataset.apiKey;
  var COUNTRY = (form.dataset.country || "").trim().toUpperCase();
  var DEFAULT_CENTER = {
    lat: parseFloat(form.dataset.defaultLat),
    lng: parseFloat(form.dataset.defaultLng)
  };
  if (isNaN(DEFAULT_CENTER.lat) || isNaN(DEFAULT_CENTER.lng)) {
    DEFAULT_CENTER = { lat: 25.2854, lng: 51.531 };
  }
  var DEFAULT_ZOOM = parseInt(form.dataset.defaultZoom, 10) || 11;
  var PICKED_ZOOM = parseInt(form.dataset.pickedZoom, 10) || 17;

  var searchWrap = root.querySelector("[data-dloc-search-wrap]");
  var searchInput = root.querySelector("[data-dloc-search]");
  var clearBtn = root.querySelector("[data-dloc-clear]");
  var suggestList = root.querySelector("[data-dloc-suggestions]");
  var geoBtn = root.querySelector("[data-dloc-geo]");
  var mapEl = root.querySelector("[data-dloc-map]");
  var mapLoading = root.querySelector("[data-dloc-map-loading]");
  var readout = root.querySelector("[data-dloc-readout]");
  var readoutArea = root.querySelector("[data-dloc-readout-area]");
  var readoutAddress = root.querySelector("[data-dloc-readout-address]");
  var errorEl = root.querySelector("[data-dloc-error]");
  var manualWrap = root.querySelector("[data-dloc-manual]");
  var manualInputs = Array.prototype.slice.call(root.querySelectorAll("[data-dloc-manual-input]"));
  var attrArea = root.querySelector("[data-dloc-attr-area]");
  var attrAddress = root.querySelector("[data-dloc-attr-address]");
  var attrCoords = root.querySelector("[data-dloc-attr-coords]");
  var attrMap = root.querySelector("[data-dloc-attr-map]");
  /* Document-wide, not scoped to the picker: on the cart page the real submit
     lives in the order summary and the mobile sticky bar, outside this root,
     reaching the form by id with <button form="…">. They all share the same
     enabled/disabled state, so they all answer to this selector. */
  var continueBtns = Array.prototype.slice.call(document.querySelectorAll("[data-dloc-continue]"));
  /* Only meaningful when pinning is compulsory. With skipping allowed the
     shopper may check out with no location at all, so greying the button out
     would contradict the skip control sitting right beside it — the buttons
     then render live and stay live. */
  var REQUIRE_LOCATION = form.dataset.requireLocation === "true";
  function setContinueEnabled(enabled) {
    continueBtns.forEach(function (button) {
      button.disabled = REQUIRE_LOCATION && !enabled;
    });
  }

  var map = null;
  var marker = null;
  var geocoder = null;
  var sessionToken = null;

  function showError(message) {
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = !message;
  }

  /* Maps couldn't start — blocked script, rejected key, billing off. Falling
     back to typed entry rather than an apology: a broken key must not lock
     every shopper out of checkout. The manual inputs ship `disabled` so they
     share attribute names with the hidden inputs without both submitting;
     swapping which pair is enabled is the whole handover. */
  var manualActive = false;
  function activateManualFallback(message) {
    if (manualActive) return;
    manualActive = true;
    if (searchWrap) searchWrap.hidden = true;
    if (geoBtn) geoBtn.hidden = true;
    var mapWrap = root.querySelector(".dloc-map-wrap");
    if (mapWrap) mapWrap.hidden = true;
    var mapHint = root.querySelector(".dloc-map-hint");
    if (mapHint) mapHint.hidden = true;
    if (readout) readout.hidden = true;

    [attrArea, attrAddress, attrCoords, attrMap].forEach(function (input) {
      if (input) input.disabled = true;
    });
    if (manualWrap) manualWrap.hidden = false;
    manualInputs.forEach(function (input) { input.disabled = false; });
    var manualArea = document.getElementById("dlocManualArea");
    if (manualArea) {
      manualArea.required = true;
      // Carry over anything already on the cart so a shopper who set a
      // location before the key broke doesn't retype it.
      if (!manualArea.value && attrArea) manualArea.value = attrArea.value;
    }
    var manualAddress = document.getElementById("dlocManualAddress");
    if (manualAddress && !manualAddress.value && attrAddress) manualAddress.value = attrAddress.value;
    setContinueEnabled(true);
    showError(message || deliveryText("map_failed", "We couldn't load the map. Please type your address instead."));
  }

  // Google calls this by name when the key is rejected. The script itself loads
  // fine in that case, so it's the only signal available. Google also logs the
  // precise reason to the console itself (RefererNotAllowedMapError,
  // ApiNotActivatedMapError, BillingNotEnabledMapError…) — this note points
  // whoever is debugging at it, since the shopper-facing copy can't.
  window.gm_authFailure = function () {
    console.error(
      "[delivery-location] Google rejected the Maps API key. See the Google error logged above for the exact reason — " +
      "most often the key's HTTP referrer restrictions don't include this origin (" + window.location.origin + "), " +
      "or Places API / Geocoding API aren't enabled on it."
    );
    activateManualFallback(deliveryText("map_failed_key", "The map couldn't be loaded. Please type your address instead."));
  };

  function loadMaps() {
    return new Promise(function (resolve, reject) {
      if (window.google && window.google.maps) { resolve(); return; }
      // Shares the loader guard with assets/blue-salon.js's store-locator map
      // so the API is never fetched twice on one page.
      var existing = document.querySelector("script[data-bs-maps-loader]");
      if (existing) {
        existing.addEventListener("load", function () { resolve(); });
        existing.addEventListener("error", function () { reject(new Error("maps script failed")); });
        return;
      }
      var callbackName = "__bsDeliveryMapsReady";
      window[callbackName] = function () { resolve(); };
      var script = document.createElement("script");
      script.src =
        "https://maps.googleapis.com/maps/api/js?key=" + encodeURIComponent(API_KEY) +
        "&libraries=places&loading=async&callback=" + callbackName;
      script.async = true;
      script.dataset.bsMapsLoader = "true";
      script.addEventListener("error", function () { reject(new Error("maps script failed")); });
      document.head.appendChild(script);
      setTimeout(function () {
        if (!(window.google && window.google.maps)) reject(new Error("maps script timed out"));
      }, 12000);
    });
  }

  /* The store-locator map may have loaded the API first, without the places
     library — pull it in rather than assuming it's there. */
  function ensurePlaces() {
    if (window.google && window.google.maps && window.google.maps.places) return Promise.resolve();
    if (window.google && window.google.maps && typeof window.google.maps.importLibrary === "function") {
      return window.google.maps.importLibrary("places").catch(function () { /* geocoder tier covers it */ });
    }
    return Promise.resolve();
  }

  /* ── Address normalisation ──────────────────────────────────
     Everything resolves through the Geocoder, so there's exactly one result
     shape to read: `address_components` + `formatted_address` + `geometry`. */
  var AREA_TYPES = [
    "locality",
    "postal_town",
    "administrative_area_level_2",
    "sublocality_level_1",
    "sublocality",
    "administrative_area_level_1"
  ];

  function areaFromComponents(components, fallbackAddress) {
    var list = components || [];
    for (var i = 0; i < AREA_TYPES.length; i++) {
      for (var j = 0; j < list.length; j++) {
        if ((list[j].types || []).indexOf(AREA_TYPES[i]) !== -1) return list[j].long_name;
      }
    }
    // No administrative component at all (a plus code, open desert) — the head
    // of the formatted address is better than an empty area, which would leave
    // the bag with nothing to display and the gate unlocked by a blank value.
    return (fallbackAddress || "").split(",")[0].trim();
  }

  function geocode(request) {
    return new Promise(function (resolve, reject) {
      geocoder.geocode(request, function (results, status) {
        if (status === "OK" && results && results.length) resolve(results);
        else reject(new Error("geocode " + status));
      });
    });
  }

  function locationFromResult(result) {
    var position = result.geometry.location;
    return {
      lat: typeof position.lat === "function" ? position.lat() : position.lat,
      lng: typeof position.lng === "function" ? position.lng() : position.lng,
      address: result.formatted_address || "",
      area: areaFromComponents(result.address_components, result.formatted_address)
    };
  }

  /* ── Applying a chosen location ────────────────────────────── */
  function setLocation(loc, options) {
    var opts = options || {};
    var coords = loc.lat.toFixed(6) + "," + loc.lng.toFixed(6);
    if (attrArea) attrArea.value = loc.area || "";
    if (attrAddress) attrAddress.value = loc.address || "";
    if (attrCoords) attrCoords.value = coords;
    // Six decimals is ~0.1m — far finer than a doorway, and short enough that
    // the link stays readable in the admin's Additional details card.
    if (attrMap) attrMap.value = "https://www.google.com/maps?q=" + coords;

    if (readoutArea) readoutArea.textContent = loc.area || "";
    if (readoutAddress) readoutAddress.textContent = loc.address || "";
    if (readout) readout.hidden = !(loc.area || loc.address);

    /* Mirror the resolved address back into the search field, so dragging the
       pin (or restoring a saved location) updates the box the shopper typed in
       rather than leaving it stale or empty. Assigning `value` doesn't fire an
       `input` event, so this can't kick off another round of suggestions. */
    if (searchInput && loc.address) {
      searchInput.value = loc.address;
      if (clearBtn) clearBtn.hidden = false;
    }
    setContinueEnabled(true);
    showError("");

    if (map) {
      var position = { lat: loc.lat, lng: loc.lng };
      if (marker) {
        marker.setPosition(position);
        // Revealed here, not at each caller — the marker is built hidden (there's
        // nothing to point at on an untouched map) and this is the one place a
        // location is ever applied, so picking a search suggestion drops the pin
        // just like tapping the map does. The callers that also call setVisible
        // do it for instant feedback while the reverse geocode is still in
        // flight, which this can't cover.
        marker.setVisible(true);
      }
      if (!opts.keepView) {
        map.setCenter(position);
        // Only zoom in on a fresh pick — re-zooming after every pin drag would
        // fight the shopper for control of the view.
        if (opts.zoomIn !== false) map.setZoom(Math.max(map.getZoom() || 0, PICKED_ZOOM));
      }
    }
  }

  function pickLatLng(lat, lng, opts) {
    return geocode({ location: { lat: lat, lng: lng } })
      .then(function (results) {
        setLocation(locationFromResult(results[0]), opts);
      })
      .catch(function () {
        // Reverse geocoding failed but the point is still valid and still
        // deliverable — keep the coordinates and say the area is unknown rather
        // than discarding what the shopper chose.
        setLocation({
          lat: lat,
          lng: lng,
          address: lat.toFixed(6) + ", " + lng.toFixed(6),
          area: "Pinned location"
        }, opts);
      });
  }

  /* ── Suggestions ───────────────────────────────────────────── */
  function textOf(value) {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (typeof value.text === "string") return value.text;
    if (typeof value.toString === "function") return value.toString();
    return "";
  }

  function newSessionToken() {
    try {
      var places = window.google.maps.places;
      if (places && places.AutocompleteSessionToken) sessionToken = new places.AutocompleteSessionToken();
    } catch (err) {
      sessionToken = null;
    }
  }

  /* Last-resort suggestions: no Places at all, just the Geocoder answering a
     typed address. Needs a fuller address than a prediction would, but it keeps
     the field working on a key that only has Geocoding enabled. */
  function geocoderSuggestions(term) {
    var request = { address: term };
    if (COUNTRY) request.componentRestrictions = { country: COUNTRY };
    return geocode(request)
      .then(function (results) {
        return results.slice(0, 6).map(function (result) {
          return {
            result: result,
            main: (result.formatted_address || "").split(",")[0],
            secondary: (result.formatted_address || "").split(",").slice(1).join(",").trim()
          };
        });
      })
      .catch(function () { return []; });
  }

  /* The tiers degrade on RESULTS, not just on capability. The classes below
     exist in the Maps JS library whether or not the matching API is enabled on
     the key's project, so "the class is there" proves nothing — a project with
     only Geocoding enabled still constructs an AutocompleteSuggestion request
     and gets it rejected at call time. Hence each tier falls through to the next
     on an error or an empty list. */
  function fetchSuggestions(term) {
    var places = (window.google && window.google.maps && window.google.maps.places) || null;

    // Tier 1 — the current Places autocomplete data API (Places API (New)).
    if (places && places.AutocompleteSuggestion && places.AutocompleteSuggestion.fetchAutocompleteSuggestions) {
      var request = { input: term };
      if (sessionToken) request.sessionToken = sessionToken;
      if (COUNTRY) request.includedRegionCodes = [COUNTRY];
      return places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request)
        .then(function (response) {
          return (response.suggestions || [])
            .map(function (item) { return item.placePrediction; })
            .filter(Boolean)
            .map(function (prediction) {
              return {
                placeId: prediction.placeId,
                main: textOf(prediction.mainText) || textOf(prediction.text),
                secondary: textOf(prediction.secondaryText)
              };
            });
        })
        .then(function (list) { return list.length ? list : legacySuggestions(term); })
        .catch(function () { return legacySuggestions(term); });
    }

    return legacySuggestions(term);
  }

  // Tier 2 — the legacy Places service. Newer Google Cloud projects can't use
  // it at all ("You're calling a legacy API"), so a failure here is expected
  // rather than exceptional; it's kept for older keys where it's the only
  // prediction source available.
  function legacySuggestions(term) {
    var places = (window.google && window.google.maps && window.google.maps.places) || null;
    if (!places || !places.AutocompleteService) return geocoderSuggestions(term);
    return new Promise(function (resolve) {
      var service = new places.AutocompleteService();
      var request = { input: term };
      if (sessionToken) request.sessionToken = sessionToken;
      if (COUNTRY) request.componentRestrictions = { country: COUNTRY.toLowerCase() };
      service.getPlacePredictions(request, function (predictions, status) {
        if (status !== "OK" || !predictions || !predictions.length) {
          resolve(geocoderSuggestions(term));
          return;
        }
        resolve(predictions.map(function (prediction) {
          var parts = prediction.structured_formatting || {};
          return {
            placeId: prediction.place_id,
            main: parts.main_text || prediction.description,
            secondary: parts.secondary_text || ""
          };
        }));
      });
    });
  }

  var activeIndex = -1;

  function closeSuggestions() {
    if (!suggestList) return;
    suggestList.hidden = true;
    suggestList.innerHTML = "";
    activeIndex = -1;
    if (searchInput) searchInput.setAttribute("aria-expanded", "false");
  }

  function chooseSuggestion(suggestion) {
    closeSuggestions();
    // The prediction's own text, shown immediately; setLocation replaces it with
    // Google's canonical formatted address once the geocode comes back.
    if (searchInput) searchInput.value = [suggestion.main, suggestion.secondary].filter(Boolean).join(", ");
    // A prediction carries no coordinates, so the Geocoder resolves its
    // placeId — the same call that resolves everything else here.
    var resolve = suggestion.result
      ? Promise.resolve([suggestion.result])
      : geocode({ placeId: suggestion.placeId });
    resolve
      .then(function (results) {
        setLocation(locationFromResult(results[0]));
        newSessionToken();
      })
      .catch(function () {
        showError(deliveryText("geocode_failed", "We couldn't pinpoint that address. Try another, or drop the pin on the map."));
      });
  }

  function renderSuggestions(list) {
    if (!suggestList) return;
    if (!list.length) { closeSuggestions(); return; }
    suggestList.innerHTML = "";
    list.forEach(function (suggestion, index) {
      var li = document.createElement("li");
      li.className = "dloc-suggestion";
      li.setAttribute("role", "option");
      li.id = "dlocSuggestion-" + index;
      li.setAttribute("aria-selected", "false");
      var main = document.createElement("b");
      main.textContent = suggestion.main;
      li.appendChild(main);
      if (suggestion.secondary) {
        var small = document.createElement("small");
        small.textContent = suggestion.secondary;
        li.appendChild(small);
      }
      // pointerdown, not click — the input's blur would otherwise close the
      // list before a click could land on it, and pointerdown covers mouse and
      // touch with one listener.
      li._dlocSuggestion = suggestion;
      li.addEventListener("pointerdown", function (event) {
        event.preventDefault();
        chooseSuggestion(suggestion);
      });
      suggestList.appendChild(li);
    });
    suggestList.hidden = false;
    activeIndex = -1;
    if (searchInput) searchInput.setAttribute("aria-expanded", "true");
  }

  function highlight(delta) {
    if (!suggestList || suggestList.hidden) return;
    var items = Array.prototype.slice.call(suggestList.children);
    if (!items.length) return;
    if (activeIndex >= 0 && items[activeIndex]) {
      items[activeIndex].classList.remove("is-active");
      items[activeIndex].setAttribute("aria-selected", "false");
    }
    activeIndex = (activeIndex + delta + items.length) % items.length;
    items[activeIndex].classList.add("is-active");
    items[activeIndex].setAttribute("aria-selected", "true");
    if (searchInput) searchInput.setAttribute("aria-activedescendant", items[activeIndex].id);
  }

  /* ── Boot ──────────────────────────────────────────────────── */
  loadMaps()
    .then(ensurePlaces)
    .then(function () {
      if (!window.google || !window.google.maps) throw new Error("maps unavailable");
      geocoder = new google.maps.Geocoder();
      newSessionToken();

      if (mapLoading) mapLoading.hidden = true;
      map = new google.maps.Map(mapEl, {
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        // Same brand palette as the store-locator map in blue-salon.js.
        styles: [
          { elementType: "geometry", stylers: [{ color: "#faf8f4" }] },
          { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
          { elementType: "labels.text.fill", stylers: [{ color: "#0e1722" }] },
          { elementType: "labels.text.stroke", stylers: [{ color: "#faf8f4" }] },
          { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#f3efe6" }] },
          { featureType: "poi", stylers: [{ visibility: "off" }] },
          { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
          { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#dfe3e8" }] },
          { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#002a57" }] },
          { featureType: "transit", stylers: [{ visibility: "off" }] },
          { featureType: "water", elementType: "geometry", stylers: [{ color: "#c3d3dc" }] }
        ],
        disableDefaultUI: true,
        zoomControl: true,
        gestureHandling: "greedy"
      });

      marker = new google.maps.Marker({
        map: map,
        draggable: true,
        visible: false,
        icon: {
          path: "M12 2C7.6 2 4 5.6 4 10c0 6 8 12 8 12s8-6 8-12c0-4.4-3.6-8-8-8zm0 11a3 3 0 1 1 0-6 3 3 0 0 1 0 6z",
          fillColor: "#003976",
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 1.5,
          scale: 1.6,
          anchor: new google.maps.Point(12, 22)
        }
      });

      /* On the cart page the picker starts inside a hidden panel (step 2), so
         the map is built at zero height and stays grey when the panel opens.
         Watch for it actually becoming visible and re-lay it out then — an
         observer rather than an event from cart.js, so the two files don't need
         a contract and the standalone page needs no special case. */
      if ("IntersectionObserver" in window) {
        var laidOut = false;
        var visibilityObserver = new IntersectionObserver(function (entries) {
          if (!entries[0].isIntersecting || laidOut) return;
          laidOut = true;
          google.maps.event.trigger(map, "resize");
          // Re-centre on the pin if there is one, since the resize recentres on
          // whatever the zero-height box thought its middle was.
          var position = marker && marker.getVisible() ? marker.getPosition() : null;
          map.setCenter(position || DEFAULT_CENTER);
          visibilityObserver.disconnect();
        }, { threshold: 0.01 });
        visibilityObserver.observe(mapEl);
      }

      marker.addListener("dragend", function (event) {
        // keepView so the map doesn't snap back under the shopper's hand.
        pickLatLng(event.latLng.lat(), event.latLng.lng(), { keepView: true });
      });
      map.addListener("click", function (event) {
        marker.setVisible(true);
        pickLatLng(event.latLng.lat(), event.latLng.lng(), { zoomIn: false });
      });

      /* Restore. The cart attribute wins over localStorage for the same reason
         as the area list: it's what's on the cart right now. */
      var restored = null;
      if (attrCoords && attrCoords.value) {
        restored = {
          coords: attrCoords.value,
          area: attrArea ? attrArea.value : "",
          address: attrAddress ? attrAddress.value : ""
        };
      } else {
        var stored = readStored();
        if (stored && stored.coords) restored = stored;
      }
      if (restored) {
        var parts = String(restored.coords).split(",");
        var lat = parseFloat(parts[0]);
        var lng = parseFloat(parts[1]);
        if (!isNaN(lat) && !isNaN(lng)) {
          marker.setVisible(true);
          setLocation({ lat: lat, lng: lng, area: restored.area, address: restored.address });
        }
      }

      if (searchInput) {
        var debounce = null;
        searchInput.addEventListener("input", function () {
          var term = searchInput.value.trim();
          if (clearBtn) clearBtn.hidden = !term;
          clearTimeout(debounce);
          // Under three characters the predictions are noise and each keystroke
          // is a billed request.
          if (term.length < 3) { closeSuggestions(); return; }
          debounce = setTimeout(function () {
            fetchSuggestions(term)
              .then(renderSuggestions)
              .catch(function () { closeSuggestions(); });
          }, 280);
        });

        searchInput.addEventListener("keydown", function (event) {
          if (event.key === "ArrowDown") { event.preventDefault(); highlight(1); }
          else if (event.key === "ArrowUp") { event.preventDefault(); highlight(-1); }
          else if (event.key === "Escape") { closeSuggestions(); }
          else if (event.key === "Enter") {
            // Enter must never submit the form from this field — the shopper is
            // picking an address, not confirming the order.
            event.preventDefault();
            var items = suggestList ? Array.prototype.slice.call(suggestList.children) : [];
            var active = activeIndex >= 0 ? items[activeIndex] : null;
            if (active && active._dlocSuggestion) chooseSuggestion(active._dlocSuggestion);
          }
        });

        searchInput.addEventListener("blur", function () {
          setTimeout(closeSuggestions, 120);
        });
      }

      if (clearBtn) {
        clearBtn.addEventListener("click", function () {
          searchInput.value = "";
          clearBtn.hidden = true;
          closeSuggestions();
          searchInput.focus();
        });
      }

      if (geoBtn) {
        geoBtn.addEventListener("click", function () {
          if (!navigator.geolocation) {
            showError("Your browser can't share your location. Search for your address instead.");
            return;
          }
          geoBtn.disabled = true;
          geoBtn.classList.add("is-busy");
          navigator.geolocation.getCurrentPosition(
            function (position) {
              marker.setVisible(true);
              pickLatLng(position.coords.latitude, position.coords.longitude).finally(function () {
                geoBtn.disabled = false;
                geoBtn.classList.remove("is-busy");
              });
            },
            function () {
              geoBtn.disabled = false;
              geoBtn.classList.remove("is-busy");
              showError(deliveryText("geolocation_failed", "We couldn't get your location. Please allow location access, or search for your address."));
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
          );
        });
      }
    })
    .catch(function (err) {
      console.error(err);
      activateManualFallback();
    });

  /* Skipping. Pinning is optional, so this clears the form of everything it
     would otherwise post and lets the native submit through to checkout —
     disabled inputs aren't submitted, which is exactly the "write nothing"
     behaviour wanted here. Runs on click because that fires before submit. */
  var skipping = false;
  document.querySelectorAll("[data-dloc-skip]").forEach(function (button) {
    button.addEventListener("click", function () {
      skipping = true;
      [attrArea, attrAddress, attrCoords, attrMap].forEach(function (input) {
        if (input) input.disabled = true;
      });
      manualInputs.forEach(function (input) {
        input.disabled = true;
        input.required = false;
      });
    });
  });

  form.addEventListener("submit", function (e) {
    if (skipping || manualActive) return;
    var area = attrArea ? attrArea.value.trim() : "";
    if (!area) {
      if (!REQUIRE_LOCATION) return; // checking out without a pin is allowed
      // Belt-and-braces behind the disabled button, for when pinning is
      // compulsory.
      e.preventDefault();
      showError("Please choose your delivery location first.");
      return;
    }
    writeStored({
      area: area,
      address: attrAddress ? attrAddress.value : "",
      coords: attrCoords ? attrCoords.value : ""
    });
  });
})();
